import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { ReferralSubmitData, ExperienceLevel, AvailabilityStatus } from '../types/ReferralTypes';
import { formatCurrency } from '../utils/formatters';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

interface ApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ReferralSubmitData) => Promise<void>;
  vacancyTitle: string;
  companyName: string;
  bountyAmount: number;
  bountyCurrency: string;
  darkMode?: boolean;
}

const EXPERIENCE_OPTIONS: { value: ExperienceLevel; label: string }[] = [
  { value: 'entry', label: 'Entry Level' },
  { value: 'mid', label: 'Mid Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
  { value: 'executive', label: 'Executive' },
];

const AVAILABILITY_OPTIONS: { value: AvailabilityStatus; label: string }[] = [
  { value: 'immediately', label: 'Immediately' },
  { value: '2_weeks', label: '2 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: 'not_looking', label: 'Not Actively Looking' },
];

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

const ApplicationModal: React.FC<ApplicationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  vacancyTitle,
  companyName,
  bountyAmount,
  bountyCurrency,
  darkMode,
}) => {
  const { user } = useAuth();

  const [linkedin, setLinkedin] = useState('');
  const [currentRole, setCurrentRole] = useState('');
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel | ''>('');
  const [skillsInput, setSkillsInput] = useState('');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [salaryCurrency, setSalaryCurrency] = useState('USD');
  const [availability, setAvailability] = useState<AvailabilityStatus | ''>('');
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [coverLetter, setCoverLetter] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    const skills = skillsInput
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    try {
      await onSubmit({
        candidate_name: user?.name || '',
        candidate_email: user?.email || '',
        candidate_linkedin: linkedin.trim() || undefined,
        note: note.trim() || undefined,
        type: 'self_apply',
        candidate_profile: {
          ...(skills.length > 0 && { skills }),
          ...(experienceLevel && { experience_level: experienceLevel }),
          ...(currentRole.trim() && { current_role: currentRole.trim() }),
          ...(salaryMin && { salary_min: Number(salaryMin) }),
          ...(salaryMax && { salary_max: Number(salaryMax) }),
          ...(salaryMin || salaryMax ? { salary_currency: salaryCurrency } : {}),
          ...(availability && { availability }),
          ...(portfolioUrl.trim() && { portfolio_url: portfolioUrl.trim() }),
          ...(coverLetter.trim() && { cover_letter: coverLetter.trim() }),
        },
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit application.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return ReactDOM.createPortal(
    <div className={`modal-backdrop ${darkMode ? 'dark' : ''}`} onClick={handleBackdropClick}>
      <div className="modal-content application-modal">
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>

        <h2>Apply for This Position</h2>
        <p className="modal-subtitle">
          {vacancyTitle} at {companyName}
        </p>
        <div className="modal-bounty-badge">
          Bounty: {formatCurrency(bountyAmount, bountyCurrency)}
        </div>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Your Info */}
          <div className="application-section">
            <h3 className="application-section-title">Your Info</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input type="text" value={user?.name || ''} disabled className="input-locked" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={user?.email || ''} disabled className="input-locked" />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="appLinkedin">LinkedIn Profile</label>
              <input
                id="appLinkedin"
                type="url"
                value={linkedin}
                onChange={e => setLinkedin(e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>
          </div>

          {/* Professional */}
          <div className="application-section">
            <h3 className="application-section-title">Professional</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="appCurrentRole">Current Role</label>
                <input
                  id="appCurrentRole"
                  type="text"
                  value={currentRole}
                  onChange={e => setCurrentRole(e.target.value)}
                  placeholder="Senior Software Engineer"
                />
              </div>
              <div className="form-group">
                <label htmlFor="appExperience">Experience Level</label>
                <select
                  id="appExperience"
                  value={experienceLevel}
                  onChange={e => setExperienceLevel(e.target.value as ExperienceLevel)}
                >
                  <option value="">Select...</option>
                  {EXPERIENCE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="appSkills">Skills (comma-separated)</label>
              <input
                id="appSkills"
                type="text"
                value={skillsInput}
                onChange={e => setSkillsInput(e.target.value)}
                placeholder="React, TypeScript, Node.js, AWS"
              />
            </div>
          </div>

          {/* Compensation */}
          <div className="application-section">
            <h3 className="application-section-title">Compensation</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px' }}>
              <div className="form-group">
                <label htmlFor="appSalaryMin">Salary Min</label>
                <input
                  id="appSalaryMin"
                  type="number"
                  value={salaryMin}
                  onChange={e => setSalaryMin(e.target.value)}
                  placeholder="80000"
                />
              </div>
              <div className="form-group">
                <label htmlFor="appSalaryMax">Salary Max</label>
                <input
                  id="appSalaryMax"
                  type="number"
                  value={salaryMax}
                  onChange={e => setSalaryMax(e.target.value)}
                  placeholder="120000"
                />
              </div>
              <div className="form-group">
                <label htmlFor="appSalaryCurrency">Currency</label>
                <select
                  id="appSalaryCurrency"
                  value={salaryCurrency}
                  onChange={e => setSalaryCurrency(e.target.value)}
                >
                  {CURRENCY_OPTIONS.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Additional */}
          <div className="application-section">
            <h3 className="application-section-title">Additional</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="appAvailability">Availability</label>
                <select
                  id="appAvailability"
                  value={availability}
                  onChange={e => setAvailability(e.target.value as AvailabilityStatus)}
                >
                  <option value="">Select...</option>
                  {AVAILABILITY_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="appPortfolio">Portfolio URL</label>
                <input
                  id="appPortfolio"
                  type="url"
                  value={portfolioUrl}
                  onChange={e => setPortfolioUrl(e.target.value)}
                  placeholder="https://yourportfolio.com"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="appCoverLetter">Cover Letter</label>
              <textarea
                id="appCoverLetter"
                value={coverLetter}
                onChange={e => setCoverLetter(e.target.value)}
                placeholder="Tell us why you're a great fit for this role..."
                rows={4}
              />
            </div>
          </div>

          {/* Note */}
          <div className="form-group">
            <label htmlFor="appNote">Note to Hiring Team</label>
            <textarea
              id="appNote"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Anything else you'd like to share..."
              rows={2}
            />
          </div>

          <button type="submit" className="btn-primary submit-referral-btn" disabled={loading}>
            <SendIcon sx={{ fontSize: 18, marginRight: '6px' }} />
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ApplicationModal;
